import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, CircularProgress, Typography, Alert,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Print, Close, FolderOpen } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { patientAPI, billingAPI } from '../services/api';
import { PermissionForm, CaseHistoryForm, ConsentForm } from './IPDAdmissionForms';
import OPDSlipPrint from './OPDSlipPrint';
import BillPrint from './billing/BillPrint';

/* ── helpers ────────────────────────────────────────────── */
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const calcAge = (dob) => {
  if (!dob) return '—';
  const b = new Date(dob), t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age--;
  return age;
};

const calcBMI = (weight, height) => {
  if (!weight || !height) return null;
  return (weight / Math.pow(height / 100, 2)).toFixed(1);
};

const parseAddress = (addressStr) => {
  if (!addressStr) return { city: '', state: '', country: 'India', pinCode: '' };
  const pinMatch = addressStr.match(/\b\d{6}\b/);
  const pinCode = pinMatch ? pinMatch[0] : '';
  const parts = addressStr.split(',').map(p => p.trim());
  let country = 'India';
  if (addressStr.toLowerCase().includes('india')) {
    country = 'India';
  }
  const states = ['Haryana', 'Punjab', 'Rajasthan', 'Delhi', 'Uttar Pradesh', 'UP', 'HP', 'Himachal Pradesh', 'Gujarat'];
  let state = '';
  for (const s of states) {
    if (addressStr.toLowerCase().includes(s.toLowerCase())) {
      state = s;
      break;
    }
  }
  let city = '';
  if (parts.length > 1) {
    if (state) {
      const stateIdx = parts.findIndex(p => p.toLowerCase().includes(state.toLowerCase()));
      if (stateIdx > 0) {
        city = parts[stateIdx - 1];
      }
    } else {
      city = parts[parts.length - 2] || parts[0];
    }
  } else {
    city = parts[0] || '';
  }
  return { city, state, country, pinCode };
};

/* ── shared print header ─────────────────────────────────── */
const PrintHeader = () => (
  <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 8 }}>
    <div style={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
      <img src="/logo.png" alt="MSG Logo" style={{ width: 66, height: 66, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
    </div>
    <div style={{ flex: 1, textAlign: 'center', padding: '0 6px' }}>
      <div style={{ fontSize: 16, fontWeight: 900, color: '#1b5e20', letterSpacing: 0.5 }}>MSG NATUROPATHY CENTER</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#2e7d32', marginTop: 2 }}>(MANAGED BY ARZ UNIQUE ENTERPRISES LIMITED)</div>
      <div style={{ fontSize: 10, color: '#444', marginTop: 2 }}>Sirsa, Haryana</div>
      <div style={{ fontSize: 10, color: '#444' }}>📞 +91 7082999551 / 7082999351</div>
    </div>
    <div style={{ width: 80, flexShrink: 0 }}></div>
  </div>
);

const PageBreak = () => <div style={{ pageBreakBefore: 'always', breakBefore: 'page' }} />;

const SectionBar = ({ title, color = '#1b5e20' }) => (
  <div style={{
    background: color, color: '#fff', fontWeight: 700,
    fontSize: 11, padding: '4px 10px', marginTop: 10, marginBottom: 5,
    letterSpacing: 0.5
  }}>{title}</div>
);

const BlankLines = ({ n = 3 }) => (
  <div style={{ marginTop: 4 }}>
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} style={{ borderBottom: '1px dotted #555', margin: '9px 0', minHeight: 16 }} />
    ))}
  </div>
);

const FilledText = ({ text }) => {
  if (!text) return <BlankLines n={3} />;
  return (
    <div style={{ margin: '4px 0 4px 0', lineHeight: 1.7, fontSize: 11 }}>
      {text.split('\n').map((line, i) => (
        <div key={i} style={{ borderBottom: '1px dotted #aaa', paddingBottom: 2, minHeight: 18 }}>{line || ' '}</div>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   SECTION 5 — Vitals, Diets & Treatment Summary
══════════════════════════════════════════════════════════ */
function VitalsDietTreatmentSection({ summary }) {
  const { admission, vitals, dietPlans, treatments } = summary;

  const latestVital = vitals && vitals.length > 0 ? vitals[0] : null;
  const bmi = latestVital ? calcBMI(latestVital.weight, latestVital.height) : null;

  /* group diets by date — plan_date is a string from DB */
  const dietByDate = {};
  (dietPlans || []).forEach(plan => {
    const d = plan.plan_date
      ? String(plan.plan_date).slice(0, 10)
      : 'Unknown';
    if (!dietByDate[d]) dietByDate[d] = [];
    dietByDate[d].push(plan);
  });

  /* group treatments by date + slot */
  const treatmentByDate = {};
  (treatments || []).forEach(t => {
    const d = t.plan_date ? String(t.plan_date).slice(0, 10) : 'Unknown';
    const slot = t.time_slot || 'Other';
    const key = `${d}__${slot}`;
    if (!treatmentByDate[key]) treatmentByDate[key] = { date: d, slot, items: [] };
    if (t.treatment_name && !treatmentByDate[key].items.includes(t.treatment_name)) {
      treatmentByDate[key].items.push(t.treatment_name);
    }
  });

  const slotOrder = ['Satkriya', 'Morning', 'Evening', 'Night'];
  const treatmentRows = Object.values(treatmentByDate).sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot);
  });

  const dietDates = Object.keys(dietByDate).sort();

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', padding: '14px 18px' }}>
      <PrintHeader />

      {/* Patient Strip */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 8, borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
        <tbody>
          <tr>
            <td style={{ padding: '3px 6px', borderRight: '1px solid #ccc', width: '55%' }}>
              <strong>Patient:</strong> {admission.first_name} {admission.last_name} &nbsp;|&nbsp;
              <strong>UHID:</strong> {admission.patient_code} &nbsp;|&nbsp;
              {calcAge(admission.date_of_birth)} yrs / {admission.gender || '—'}
            </td>
            <td style={{ padding: '3px 6px' }}>
              <strong>Admitted:</strong> {fmt(admission.admission_date)} &nbsp;|&nbsp;
              <strong>Discharged:</strong> {fmt(admission.discharge_date)}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{
        textAlign: 'center', background: '#1b5e20', color: '#fff',
        fontWeight: 900, fontSize: 13, padding: '5px 0', marginBottom: 8, letterSpacing: 1
      }}>
        VITALS, DIET &amp; TREATMENT RECORD
      </div>

      {/* ── VITALS TABLE ─── */}
      <SectionBar title="VITALS (Latest)" />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
        <thead>
          <tr style={{ background: '#e8f5e9' }}>
            {['BP (mmHg)', 'Pulse (bpm)', 'Resp (/min)', 'Temp (°F)', 'Height (cm)', 'Weight (kg)', 'BMI', 'SpO2 (%)'].map(h => (
              <th key={h} style={{ border: '1px solid #333', padding: '3px 5px', textAlign: 'center' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {[
              latestVital?.blood_pressure,
              latestVital?.pulse_rate,
              latestVital?.respiratory_rate,
              latestVital?.temperature ? `${latestVital.temperature}°F` : null,
              latestVital?.height ? `${latestVital.height} cm` : null,
              latestVital?.weight ? `${latestVital.weight} kg` : null,
              bmi,
              latestVital?.oxygen_saturation ? `${latestVital.oxygen_saturation}%` : null
            ].map((v, i) => (
              <td key={i} style={{ border: '1px solid #ccc', padding: '4px 5px', textAlign: 'center' }}>
                {v || <span style={{ color: '#aaa' }}>—</span>}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {vitals && vitals.length > 1 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 600, marginTop: 8, marginBottom: 3 }}>All Vitals Recorded During Admission</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Date', 'BP', 'Pulse', 'Resp', 'Temp', 'Weight', 'SpO2', 'Notes'].map(h => (
                  <th key={h} style={{ border: '1px solid #ddd', padding: '2px 4px', textAlign: 'center' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vitals.map((v, i) => (
                <tr key={i}>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{fmt(v.recorded_at || v.created_at)}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{v.blood_pressure || '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{v.pulse_rate || '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{v.respiratory_rate || '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{v.temperature || '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{v.weight || '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', textAlign: 'center' }}>{v.oxygen_saturation || '—'}</td>
                  <td style={{ border: '1px solid #eee', padding: '2px 4px', fontSize: 8 }}>{v.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ── TREATMENT PLAN TABLE ─── */}
      {treatmentRows.length > 0 && (
        <>
          <SectionBar title="TREATMENT PLAN" color="#7b1fa2" />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr style={{ background: '#f3e5f5' }}>
                <th style={{ border: '1px solid #333', padding: '3px 6px', textAlign: 'left', width: '22%' }}>Date</th>
                <th style={{ border: '1px solid #333', padding: '3px 6px', textAlign: 'left', width: '18%' }}>Time Slot</th>
                <th style={{ border: '1px solid #333', padding: '3px 6px', textAlign: 'left' }}>Treatments</th>
              </tr>
            </thead>
            <tbody>
              {treatmentRows.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#faf5ff' }}>
                  <td style={{ border: '1px solid #eee', padding: '3px 6px' }}>{fmt(row.date)}</td>
                  <td style={{ border: '1px solid #eee', padding: '3px 6px', fontWeight: 600 }}>{row.slot}</td>
                  <td style={{ border: '1px solid #eee', padding: '3px 6px' }}>{row.items.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ── DIET PLAN TABLE ─── */}
      {dietDates.length > 0 && (
        <>
          <SectionBar title="DIET PLAN" color="#e65100" />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr style={{ background: '#fff3e0' }}>
                <th style={{ border: '1px solid #333', padding: '3px 6px', textAlign: 'left', width: '22%' }}>Date</th>
                <th style={{ border: '1px solid #333', padding: '3px 6px', textAlign: 'left', width: '18%' }}>Meal Time</th>
                <th style={{ border: '1px solid #333', padding: '3px 6px', textAlign: 'left' }}>Diet Items</th>
              </tr>
            </thead>
            <tbody>
              {dietDates.map(date =>
                dietByDate[date].map((plan, j) => (
                  <tr key={`${date}-${j}`} style={{ background: j % 2 === 0 ? '#fff' : '#fffaf5' }}>
                    <td style={{ border: '1px solid #eee', padding: '3px 6px' }}>{j === 0 ? fmt(date) : ''}</td>
                    <td style={{ border: '1px solid #eee', padding: '3px 6px', fontWeight: 600 }}>{plan.meal_time}</td>
                    <td style={{ border: '1px solid #eee', padding: '3px 6px' }}>
                      {plan.items || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   SECTION 6 — Discharge Summary (inline, no dialog wrapper)
══════════════════════════════════════════════════════════ */
const PAIN_FACES = ['😄', '😊', '🙂', '😐', '😕', '😟', '😢', '😣', '😖', '😭', '😱'];
const PAIN_LABELS = ['No\nHurt', 'Hurts\nLittle Bit', 'Hurts\nLittle More', 'Hurts\nEven More', 'Hurts\nWhole Lot', 'Hurts\nWorst'];

function DischargeSummarySection({ summary }) {
  const { admission, treatments, vitals, prognosis, dischargePrescription } = summary;
  const v = vitals && vitals.length > 0 ? vitals[0] : null;
  const bmi = v ? calcBMI(v.weight, v.height) : null;
  const uniqueTreatments = [...new Set((treatments || []).map(t => t.treatment_name).filter(Boolean))];
  const diagnosis = dischargePrescription?.diagnosis || admission.reason || '';
  const courseNotes = dischargePrescription?.notes || '';
  const improvement = dischargePrescription?.improvement_percentage;
  const treatmentInstructions = dischargePrescription?.treatmentInstructions || [];
  const doctorName = dischargePrescription?.doctor_name || admission.admitted_by_name || '';
  const painScore = improvement !== null && improvement !== undefined
    ? Math.round((1 - improvement / 100) * 10) : null;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', padding: '14px 18px' }}>
      <style>{`
        table.ds-info { width: 100%; border-collapse: collapse; font-size: 11px; }
        table.ds-info td { padding: 3px 6px; vertical-align: top; }
        table.ds-vitals { width: 100%; border-collapse: collapse; font-size: 10px; }
        table.ds-vitals th, table.ds-vitals td { border: 1px solid #333; padding: 4px 6px; text-align: center; }
        table.ds-vitals th { background: #e8f5e9; font-weight: 700; }
      `}</style>

      <PrintHeader />

      {/* Patient Info */}
      <table className="ds-info" style={{ marginTop: 6, borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%', borderRight: '1px solid #ccc', paddingRight: 12 }}>
              <table style={{ width: '100%', fontSize: 11 }}><tbody>
                <tr><td style={{ paddingBottom: 3, fontWeight: 600, width: 120 }}>Patient Name</td>
                  <td>: {admission.first_name} {admission.last_name}</td></tr>
                <tr><td style={{ paddingBottom: 3, fontWeight: 600 }}>Age / Sex / Mobile</td>
                  <td>: {calcAge(admission.date_of_birth)} yrs / {admission.gender || '—'} / {admission.phone || '—'}</td></tr>
                <tr><td style={{ paddingBottom: 3, fontWeight: 600 }}>Address</td>
                  <td>: {admission.address || '—'}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Doctor's Name</td>
                  <td>: {doctorName || '—'}</td></tr>
              </tbody></table>
            </td>
            <td style={{ paddingLeft: 12 }}>
              <table style={{ width: '100%', fontSize: 11 }}><tbody>
                <tr><td style={{ paddingBottom: 3, fontWeight: 600, width: 130 }}>UHID No.</td>
                  <td>: <strong>{admission.patient_code}</strong></td></tr>
                <tr><td style={{ paddingBottom: 3, fontWeight: 600 }}>IPD No.</td>
                  <td>: {admission.id}</td></tr>
                <tr><td style={{ paddingBottom: 3, fontWeight: 600 }}>Admission Date</td>
                  <td>: {fmt(admission.admission_date)}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Discharge Date</td>
                  <td>: {fmt(admission.discharge_date)}</td></tr>
              </tbody></table>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ textAlign: 'center', background: '#1b5e20', color: '#fff', fontWeight: 900, fontSize: 13, padding: '5px 0', marginTop: 8, letterSpacing: 1 }}>
        DISCHARGE SUMMARY AND FOLLOWUP PLAN
      </div>

      <SectionBar title="VITAL DATA" />
      <table className="ds-vitals">
        <thead>
          <tr>
            {['BP (mmHg)', 'PULSE (bpm)', 'RESP (/min)', 'TEMP (°F)', 'HEIGHT (cm)', 'WEIGHT (kg)', 'BMI', 'SPO2 (%)'].map(h => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {[v?.blood_pressure, v?.pulse_rate, v?.respiratory_rate,
              v?.temperature ? `${v.temperature}°F` : null,
              v?.height ? `${v.height} cm` : null,
              v?.weight ? `${v.weight} kg` : null,
              bmi, v?.oxygen_saturation ? `${v.oxygen_saturation}%` : null
            ].map((val, i) => (
              <td key={i}>{val || <span style={{ color: '#aaa' }}>........</span>}</td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* DAILY PROGNOSIS REPORT */}
      <SectionBar title="DAILY PROGNOSIS REPORT" />
      {!prognosis || prognosis.length === 0 ? (
        <div style={{ fontSize: 10, fontStyle: 'italic', color: '#666', padding: '6px 8px', border: '1px solid #333', textAlign: 'center' }}>
          No prognosis records recorded during this admission
        </div>
      ) : (
        <table className="ds-vitals" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', width: '120px' }}>Date & Time</th>
              <th style={{ textAlign: 'left' }}>Progressive Note</th>
              <th style={{ textAlign: 'left' }}>Doctor's Order</th>
              <th style={{ textAlign: 'left', width: '120px' }}>Recorded By</th>
            </tr>
          </thead>
          <tbody>
            {prognosis.map((p, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                  {new Date(p.recorded_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
                <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>{p.progressive_note || '-'}</td>
                <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>{p.doctors_order || '-'}</td>
                <td style={{ textAlign: 'left' }}>
                  {p.recorded_by_name || '-'} {p.recorded_by_role ? `(${p.recorded_by_role})` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1, border: '1px solid #333', padding: '4px 8px' }}>
          <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 4, background: '#e8f5e9', padding: '2px 4px' }}>MAIN COMPLAINT</div>
          <FilledText text={diagnosis} />
        </div>
        <div style={{ width: 280, border: '1px solid #333', padding: '4px 8px' }}>
          <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, background: '#e8f5e9', padding: '2px 4px', textAlign: 'center' }}>PAIN MEASUREMENT SCALE</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            {PAIN_FACES.map((face, i) => (
              <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: painScore === i ? 18 : 13, lineHeight: 1, filter: painScore !== null && painScore !== i ? 'grayscale(1) opacity(0.4)' : 'none' }}>{face}</div>
                <div style={{ fontSize: 8, lineHeight: 1, marginTop: 1 }}>{i}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
            {PAIN_LABELS.map((label, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 7, color: '#555', lineHeight: 1.2, flex: 1, whiteSpace: 'pre-line' }}>{label}</div>
            ))}
          </div>
        </div>
      </div>

      <SectionBar title="COURSE IN HOSPITAL" />
      <FilledText text={courseNotes || (
        `Patient was admitted on ${fmt(admission.admission_date)} with ${diagnosis}.` +
        (uniqueTreatments.length > 0 ? ` Patient received naturopathy treatments including ${uniqueTreatments.slice(0, 5).join(', ')}.` : '') +
        ` The patient's health status at the time of discharge is stable with satisfactory improvement.` +
        (improvement !== null && improvement !== undefined ? ` Patient showed ${improvement}% improvement during the course of treatment.` : '')
      )} />

      <SectionBar title="TREATMENT GIVEN" />
      <FilledText text={uniqueTreatments.length > 0 ? uniqueTreatments.join('\n') : ''} />

      <SectionBar title="INVESTIGATIONS" />
      <BlankLines n={3} />

      {/* Page 2 */}
      <PageBreak />
      <PrintHeader />

      <div style={{ textAlign: 'center', background: '#1b5e20', color: '#fff', fontWeight: 900, fontSize: 13, padding: '5px 0', marginTop: 8, letterSpacing: 1 }}>
        DISCHARGE SUMMARY AND FOLLOWUP PLAN (Contd.)
      </div>

      <SectionBar title="DISCHARGE IMPROVEMENT" />
      {improvement !== null && improvement !== undefined ? (
        <div style={{ padding: '6px 8px', fontSize: 11 }}>
          <span style={{ fontWeight: 700 }}>Overall Improvement: </span>
          <span style={{ fontSize: 14, fontWeight: 900, color: '#1b5e20' }}>{improvement}%</span>
          <div style={{ marginTop: 6 }}>
            <div style={{ background: '#e0e0e0', borderRadius: 4, height: 14, position: 'relative', overflow: 'hidden' }}>
              <div style={{ background: '#2e7d32', width: `${improvement}%`, height: '100%', borderRadius: 4 }} />
            </div>
          </div>
          <BlankLines n={2} />
        </div>
      ) : <BlankLines n={3} />}

      <SectionBar title="SUGGESTED TREATMENT" />
      {treatmentInstructions.length > 0 ? (
        <FilledText text={treatmentInstructions.map(t =>
          `${t.treatment_name}${t.frequency ? ` — ${t.frequency}` : ''}${t.duration ? `, ${t.duration}` : ''}${t.instructions ? ` (${t.instructions})` : ''}`
        ).join('\n')} />
      ) : <BlankLines n={4} />}

      <SectionBar title="SUGGESTED YOGA" />
      <BlankLines n={4} />

      <SectionBar title="FOLLOW UP CONTACT DETAILS" />
      <div style={{ padding: '6px 10px', fontSize: 11, lineHeight: 1.8 }}>
        <div>• For appointment and follow-up query: <strong>+91 7082999551 / 7082999351</strong> (Timing: 8 AM – 5 PM)</div>
        <div>• Email: <strong>msgelite7wonders@gmail.com</strong></div>
        <div>• Please continue practicing the yoga techniques taught during your stay at home.</div>
        <div>• Please continue diet therapy as advised by our dietitian.</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 36, padding: '0 10px' }}>
        <div style={{ textAlign: 'center', width: '40%' }}>
          <div style={{ borderTop: '1.5px solid #333', paddingTop: 4, marginTop: 30 }}>Patient / Attender Signature</div>
        </div>
        <div style={{ textAlign: 'center', width: '40%' }}>
          <div style={{ fontSize: 10, marginBottom: 2 }}>{doctorName}</div>
          <div style={{ borderTop: '1.5px solid #333', paddingTop: 4, marginTop: 26 }}>Doctor's Signature &amp; Stamp</div>
        </div>
      </div>

      <div style={{ textAlign: 'center', color: '#c62828', fontWeight: 700, fontSize: 12, marginTop: 20, borderTop: '1px solid #ccc', paddingTop: 8 }}>
        Note — Not For Medico Legal Purpose
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   SECTION 8 — Feedback Form
══════════════════════════════════════════════════════════ */
function FeedbackForm({ patient, admission }) {
  const fullName = patient
    ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim()
    : (admission ? `${admission.first_name || ''} ${admission.last_name || ''}`.trim() : '');

  const FL = ({ value = '', minWidth = 180 }) => (
    <span style={{ borderBottom: '1px solid #333', display: 'inline-block', minWidth, paddingBottom: 1, lineHeight: 1.4, verticalAlign: 'bottom' }}>
      {value || '\u00A0'}
    </span>
  );

  const ratings = [
    'Overall Experience',
    'Quality of Treatment / Naturopathy Services',
    'Doctor\'s Consultation & Care',
    'Nursing & Staff Behaviour',
    'Cleanliness & Hygiene (Room / Centre)',
    'Diet & Nutrition Services',
    'Yoga & Wellness Sessions',
    'Administrative / Billing Process',
    'Value for Money'
  ];

  const cols = ['Excellent', 'Good', 'Average', 'Poor'];

  return (
    <div style={{ fontFamily: 'Times New Roman, serif', fontSize: 12, color: '#111' }}>
      <div style={{ border: '2px solid #333', padding: '10px 14px' }}>
        {/* Hospital Header */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ width: 88, verticalAlign: 'middle', padding: '4px 6px' }}>
                <img src="/logo.png" alt="MSG Logo" style={{ width: 76, height: 76, objectFit: 'contain' }} />
              </td>
              <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '4px 0' }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', marginTop: 2 }}>(MANAGED BY ARZ UNIQUE ENTERPRISES LIMITED)</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1b5e20', marginTop: 2 }}>Sirsa, Haryana</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1b5e20', marginTop: 2 }}>PHONE- +91 7082999551 / +91 7082999351</div>
              </td>
              <td style={{ width: 88, verticalAlign: 'middle', padding: '4px 6px' }}></td>
            </tr>
          </tbody>
        </table>
        <hr style={{ border: '1px solid #333', margin: '6px 0' }} />

        <div style={{ border: '1px solid #333', textAlign: 'center', fontWeight: 700, fontSize: 13, padding: '5px 6px', margin: '10px 0 8px 0', background: '#f0f0f0', textDecoration: 'underline' }}>
          PATIENT FEEDBACK FORM — पेशेंट फीडबैक फार्म
        </div>

        <div style={{ fontSize: 11, color: '#555', textAlign: 'center', marginBottom: 12 }}>
          Dear Health Seeker, your feedback helps us improve our services. Please take a moment to share your experience.
        </div>

        {/* Patient Info */}
        <div style={{ lineHeight: 2.1, marginBottom: 10 }}>
          <div>
            <strong>Patient Name:</strong>&nbsp;<FL value={fullName} minWidth={220} />&nbsp;&nbsp;
            <strong>UHID:</strong>&nbsp;<FL value={admission?.patient_code || ''} minWidth={130} />
          </div>
          <div>
            <strong>Admission Date:</strong>&nbsp;<FL value={admission ? fmt(admission.admission_date) : ''} minWidth={120} />&nbsp;&nbsp;
            <strong>Discharge Date:</strong>&nbsp;<FL value={admission ? fmt(admission.discharge_date) : ''} minWidth={120} />&nbsp;&nbsp;
            <strong>Room No:</strong>&nbsp;<FL value={admission?.room_number || ''} minWidth={80} />
          </div>
        </div>

        {/* Rating Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 10 }}>
          <thead>
            <tr style={{ background: '#e8f5e9' }}>
              <th style={{ border: '1px solid #999', padding: '5px 8px', textAlign: 'left', width: '50%' }}>Parameter</th>
              {cols.map(c => (
                <th key={c} style={{ border: '1px solid #999', padding: '5px 8px', textAlign: 'center', width: '12.5%' }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ratings.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9faf9' }}>
                <td style={{ border: '1px solid #ccc', padding: '6px 8px' }}>{i + 1}.&nbsp;{r}</td>
                {cols.map(c => (
                  <td key={c} style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '1.5px solid #555', borderRadius: '50%' }}></span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Open Feedback */}
        <div style={{ marginBottom: 8 }}>
          <strong>What did you like most about our services?</strong>
          {[0, 1].map(i => <div key={i} style={{ borderBottom: '1px solid #555', margin: '10px 0', minHeight: 14 }} />)}
        </div>
        <div style={{ marginBottom: 8 }}>
          <strong>What can we improve?</strong>
          {[0, 1].map(i => <div key={i} style={{ borderBottom: '1px solid #555', margin: '10px 0', minHeight: 14 }} />)}
        </div>
        <div style={{ marginBottom: 8 }}>
          <strong>Any other suggestions or comments:</strong>
          {[0, 1].map(i => <div key={i} style={{ borderBottom: '1px solid #555', margin: '10px 0', minHeight: 14 }} />)}
        </div>

        {/* Would recommend */}
        <div style={{ marginBottom: 14 }}>
          <strong>Would you recommend MSG Naturopathy Center to others?</strong>&nbsp;&nbsp;
          {['Yes', 'No', 'Maybe'].map(opt => (
            <span key={opt} style={{ marginRight: 18 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, border: '1.5px solid #555', borderRadius: '50%', verticalAlign: 'middle', marginRight: 4 }}></span>
              {opt}
            </span>
          ))}
        </div>

        {/* Signatures */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
          <div style={{ textAlign: 'center', width: '40%' }}>
            <div style={{ borderBottom: '1px solid #333', marginBottom: 4, minHeight: 30 }}></div>
            <div style={{ fontSize: 11 }}>Date</div>
          </div>
          <div style={{ textAlign: 'center', width: '40%' }}>
            <div style={{ borderBottom: '1px solid #333', marginBottom: 4, minHeight: 30 }}></div>
            <div style={{ fontSize: 11 }}>Patient / Attendant Signature</div>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 11, marginTop: 14, color: '#555', fontStyle: 'italic' }}>
          Thank you for choosing MSG Naturopathy Center. Wishing you continued good health!
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN — PatientFilePrint
══════════════════════════════════════════════════════════ */
function PatientFilePrint({ admissionId, patientId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [bill, setBill] = useState(null);
  const [printSelection, setPrintSelection] = useState('all');
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: summary
      ? `PatientFile_${summary.admission?.patient_code}_${new Date().toISOString().split('T')[0]}`
      : 'PatientFile',
    pageStyle: `
      @page { size: A4; margin: 12mm 14mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    `
  });

  useEffect(() => {
    loadData();
  }, [admissionId, patientId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryRes, billsRes] = await Promise.all([
        patientAPI.getDischargeSummary(admissionId),
        billingAPI.getPatientBills(patientId)
      ]);

      const summaryData = summaryRes.data.data;
      setSummary(summaryData);

      /* Find the IPD bill for this admission and load its full details */
      const bills = billsRes.data.data || [];
      const ipdBills = bills.filter(b => b.bill_type === 'ipd');
      if (ipdBills.length > 0) {
        try {
          const billDetailRes = await billingAPI.getBillWithPayments(ipdBills[0].id);
          setBill(billDetailRes.data.data || billDetailRes.data);
        } catch {
          /* bill load failed, skip */
        }
      }
    } catch (err) {
      setError('Failed to load patient data. Please try again.');
      console.error('PatientFilePrint load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogContent>
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={200} gap={2}>
            <CircularProgress />
            <Typography>Loading patient file...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !summary) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">{error || 'No data available'}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const { admission } = summary;

  /* Adapt admission data to match PermissionForm / CaseHistoryForm / ConsentForm's expected shape */
  const patientForForms = {
    first_name: admission.first_name,
    last_name: admission.last_name,
    firstName: admission.first_name,
    lastName: admission.last_name,
    phone: admission.phone,
    address: admission.address,
    date_of_birth: admission.date_of_birth,
    dateOfBirth: admission.date_of_birth,
    email: admission.email,
    gender: admission.gender,
    patientCode: admission.patient_code,
    patient_code: admission.patient_code,
    chiefComplaint: admission.chief_complaint || '',
    chief_complaint: admission.chief_complaint || ''
  };

  const admissionForForms = {
    admissionDate: admission.admission_date,
    roomNumber: admission.room_number,
    diagnosis: summary.dischargePrescription?.diagnosis || admission.reason || '',
    patient_code: admission.patient_code
  };

  const parsedAddress = parseAddress(admission.address || '');
  let formOverrides = {
    fatherName: admission.father_husband_name || '',
    city: parsedAddress.city,
    state: parsedAddress.state,
    country: parsedAddress.country,
    pinCode: parsedAddress.pinCode
  };

  if (admission.form_overrides) {
    const saved = typeof admission.form_overrides === 'string'
      ? JSON.parse(admission.form_overrides)
      : admission.form_overrides;
    formOverrides = { ...formOverrides, ...saved };
  }

  /* OPD slip vitals — use first recorded vitals for admission */
  const firstVital = summary.vitals && summary.vitals.length > 0
    ? summary.vitals[summary.vitals.length - 1]  /* last in desc-sorted = earliest */
    : null;

  const vitalsForSlip = firstVital ? {
    bloodPressure: firstVital.blood_pressure,
    pulseRate: firstVital.pulse_rate,
    respiratoryRate: firstVital.respiratory_rate,
    temperature: firstVital.temperature,
    height: firstVital.height,
    weight: firstVital.weight,
    oxygenSaturation: firstVital.oxygen_saturation,
    painScale: firstVital.pain_scale
  } : {};

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderOpen color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Patient File — {admission.first_name} {admission.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              UHID: {admission.patient_code} &nbsp;|&nbsp; Admitted: {fmt(admission.admission_date)} &nbsp;|&nbsp; Discharged: {fmt(admission.discharge_date)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <div ref={printRef}>
          <style>{`
            @media print {
              .page-break { page-break-before: always; break-before: page; }
              .no-print { display: none !important; }
            }
            .page-break { margin: 0; padding: 0; }
          `}</style>

          {/* ── 1. Permission for Admission ────────────────── */}
          {(printSelection === 'all' || printSelection === 'permission') && (
            <div style={{ padding: '14px 18px' }}>
              <PermissionForm patient={patientForForms} admission={admissionForForms} overrides={formOverrides} />
            </div>
          )}

          {/* ── 2. OPD Slip ─────────────────────────────────── */}
          {(printSelection === 'all' || printSelection === 'opd_slip') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <OPDSlipPrint
                patient={{
                  firstName: admission.first_name,
                  lastName: admission.last_name,
                  dateOfBirth: admission.date_of_birth,
                  gender: admission.gender,
                  phone: admission.phone,
                  address: admission.address,
                  patientCode: admission.patient_code,
                  chiefComplaint: admission.chief_complaint || ''
                }}
                visitInfo={{ opdId: '', yearlyId: '' }}
                vitals={vitalsForSlip}
                consultationInfo={{
                  doctorName: admission.admitted_by_name || '',
                  diagnosis: summary.dischargePrescription?.diagnosis || admission.diagnosis || '',
                  notes: admission.doctor_notes || '',
                  treatments: [],
                  packages: []
                }}
              />
            </>
          )}

          {/* ── 3. Case History ──────────────────────────────── */}
          {(printSelection === 'all' || printSelection === 'case_history') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <div style={{ padding: '14px 18px' }}>
                <CaseHistoryForm
                  patient={patientForForms}
                  overrides={{
                    presentComplaint:   admission.chief_complaint || '',
                    pastHistory:        admission.past_medical_history || '',
                    currentMedications: admission.current_medications || '',
                    allergies:          admission.allergies || '',
                    caseHistoryDiagnosis: admissionForForms.diagnosis,
                    doctorNotes: admission.doctor_notes || admission.clinical_notes || '',
                    ...formOverrides
                  }}
                />
              </div>
            </>
          )}

          {/* ── 4. Consent Form ──────────────────────────────── */}
          {(printSelection === 'all' || printSelection === 'consent') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <div style={{ padding: '14px 18px' }}>
                <ConsentForm patient={patientForForms} overrides={formOverrides} />
              </div>
            </>
          )}

          {/* ── 5. Vitals, Diets & Treatment ─────────────────── */}
          {(printSelection === 'all' || printSelection === 'vitals_diet') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <VitalsDietTreatmentSection summary={summary} />
            </>
          )}

          {/* ── 6. Discharge Summary ─────────────────────────── */}
          {(printSelection === 'all' || printSelection === 'discharge_summary') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <DischargeSummarySection summary={summary} />
            </>
          )}

          {/* ── 7. Billing ───────────────────────────────────── */}
          {bill && (printSelection === 'all' || printSelection === 'bill') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <div style={{ padding: '6px' }}>
                <BillPrint bill={bill} />
              </div>
            </>
          )}

          {/* ── 8. Feedback Form ─────────────────────────────── */}
          {(printSelection === 'all' || printSelection === 'feedback') && (
            <>
              {printSelection === 'all' && <div className="page-break" />}
              <div style={{ padding: '14px 18px' }}>
                <FeedbackForm patient={patientForForms} admission={{ ...admissionForForms, ...admission }} />
              </div>
            </>
          )}
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
          Select section to print or print full file.
        </Typography>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="file-print-select-label">Section to Print</InputLabel>
          <Select
            labelId="file-print-select-label"
            value={printSelection}
            onChange={(e) => setPrintSelection(e.target.value)}
            label="Section to Print"
          >
            <MenuItem value="all">Full Patient File (All)</MenuItem>
            <MenuItem value="permission">Permission for Admission</MenuItem>
            <MenuItem value="opd_slip">OPD Slip</MenuItem>
            <MenuItem value="case_history">Patient Case History</MenuItem>
            <MenuItem value="consent">Consent Form</MenuItem>
            <MenuItem value="vitals_diet">Vitals, Diets & Treatments</MenuItem>
            <MenuItem value="discharge_summary">Discharge Summary</MenuItem>
            {bill && <MenuItem value="bill">Billing Invoice</MenuItem>}
            <MenuItem value="feedback">Feedback Form</MenuItem>
          </Select>
        </FormControl>

        <Button onClick={onClose} startIcon={<Close />} variant="outlined">Close</Button>
        <Button onClick={handlePrint} startIcon={<Print />} variant="contained" color="primary">
          {printSelection === 'all' ? 'Print Complete File' : 'Print Selected Section'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PatientFilePrint;
